import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

export const useAppUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Listen for service worker updates
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('[App] Service Worker controller changed');
        window.location.reload();
      });

      // Check for waiting service worker
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          setUpdateAvailable(true);
        }

        // Listen for new service worker installing
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
                toast({
                  title: "Actualización disponible",
                  description: "Hay una nueva versión de la aplicación disponible.",
                  duration: 10000,
                });
              }
            });
          }
        });
      });

      // Check for updates periodically
      const checkForUpdates = () => {
        navigator.serviceWorker.ready.then((registration) => {
          registration.update();
        });
      };

      // Check for updates every 30 seconds when tab is visible
      const interval = setInterval(() => {
        if (!document.hidden) {
          checkForUpdates();
        }
      }, 30000);

      // Check for updates when tab becomes visible
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          checkForUpdates();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [toast]);

  const applyUpdate = async () => {
    if (!('serviceWorker' in navigator)) return;

    setIsUpdating(true);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      
      if (registration.waiting) {
        // Tell the waiting service worker to skip waiting
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      } else {
        // Force reload if no waiting worker
        window.location.reload();
      }
    } catch (error) {
      console.error('[App] Error applying update:', error);
      window.location.reload();
    }
  };

  return {
    updateAvailable,
    isUpdating,
    applyUpdate
  };
};